import React, { useEffect, useState } from "react";

// eslint-disable-next-line import/no-extraneous-dependencies
import Tour from "reactour";
import {
    RiAddFill,
    RiAddLine, RiCommandFill, RiFeedbackLine, RiPassportFill, RiQuestionLine, RiSearchLine,
} from "@remixicon/react";
import useCurrentSession from "../../../contexts/session";
import { useTheme } from "../../../contexts/Theme";
import Styles from "./Tour.module.scss";
import { tryParseDate, DAY } from "../../../date";
import Button from "../../theme/button/Button";
import { useTicketState } from "../../../contexts/ticket-state";
import { DashboardView, useCreateTicketMutation } from "../../../__generated__/api";
import usePortal from "../../../contexts/PortalContext";
import useRouting, { Paths, useParams } from "../../../contexts/Routing";

function StepDashboard() {
    const ticketContext = useTicketState();
    const session = useCurrentSession();
    const { historyPush } = useRouting();

    const isEmpty = ticketContext.dashboardView?.getData().every(column => column.groups.isEmpty);

    const [createTickets] = useCreateTicketMutation();
    const createSampleTickets = async () => {
        const created = await createTickets({
            variables: {
                ticket: {
                    subject: "Sample Ticket",
                    description: "This is a sample ticket created for the tour.",
                    requestorId: session.userId,
                },
            },
        });

        historyPush(Paths.AGENT_CHAT, {
            chatId: created.data?.saveTicket.ticketNumber?.toString() as string,
        });
    };

    return (
        <div className={Styles.body}>
            <p>
                Use this board to manage your tickets, take actions by dragging and dropping or by selecting the
                circlular icon in the bottom right hand corner of each ticket to take action on a multiple tickets at once.
            </p>
            {isEmpty ? (
                <div className={Styles["create-ticket"]}>
                    <p>
                        You do not have any tickets. Create a sample ticket to continue the tour.
                    </p>
                    <Button size="small" prefixIcon={<RiAddLine />} onClick={createSampleTickets}>
                        Create Sample Tickets
                    </Button>
                </div>
            ) : (
                <div className={Styles["create-ticket"]}>
                    Click on any ticket to continue the tour.
                </div>
            )}
        </div>
    );
}

const steps = [
    {
        title: "Welcome",
        selector: "default",
        navDotAriaLabel: "Welcome",
        content: (
            <div className={Styles.body}>
                <h3>
                    Welcome to the New Foqal!
                </h3>
                <p>
                    This is a quick tour of the new Foqal interface and functionality.
                    Whether this is your first time or you are a returning user,
                    we hope you enjoy the new experience. So lets talk about some
                    of the new features.
                </p>
            </div>
        ),
    },
    {
        title: "Views and Filters",
        selector: "[data-tour-panel=\"board-top\"]",
        navDotAriaLabel: "Top Panel",
        content: (
            <div className={Styles.body}>
                <p>
                    Here you can store your personalized views. See your tickets
                    in a Kanban or List view, and apply different filters, sorting, and
                    grouping.
                </p>
                <p>
                    Want to store a set of tickets so you can keep coming back to it? Click the
                    {" "}
                    <RiAddFill />
                    {" "}
                    to create a new view, configure the options, and get back to the list whenever you want.
                </p>
            </div>
        ),

    },
    {
        title: "Search and Create",
        selector: "[data-tour-panel=\"search-create\"]",
        navDotAriaLabel: "Search and Create",
        content: (
            <div className={Styles.body}>
                <p>
                    Use this part of the interface to help you with the some common tasks
                    - creating tickets, finding tickets, and getting help.
                </p>
                <ul className={Styles["button-list"]}>
                    <li>
                        <RiSearchLine />
                        {" "}
                        - Search for your ticket history
                    </li>
                    <li>
                        <RiCommandFill />
                        {" "}
                        - View the keyboard Shortcuts
                    </li>
                    <li>
                        <RiQuestionLine />
                        {" "}
                        - Get contexual help based on what you are looking for
                    </li>
                    <li>
                        <RiFeedbackLine />
                        {" "}
                        - Send us product feedback or tell us how you like Foqal.
                    </li>
                    <li>
                        <Button size="xsmall" prefixIcon={<RiAddLine />}>
                            Create
                        </Button>
                        {" "}
                        - Start a new converation or create a new ticket.
                    </li>
                </ul>

            </div>
        ),

    },
    {
        title: "View and Manage",
        selector: "[data-tour-panel=\"board-main\"]",
        navDotAriaLabel: "Main Board",
        stepInteraction: true,
        content: <StepDashboard />,
    },
    {
        title: "Manage Conversation",
        selector: "[data-tour-panel=\"chat-messages\"]",
        navDotAriaLabel: "Manage Conversation",
        stepInteraction: true,
        content: (
            <div className={Styles.body}>
                Here you can see the history of the conversation - all the messages from the users, other agents, and system
                messages about what happened to the conversation
            </div>
        ),
    },
    {
        title: "Respond and add notes",
        selector: "[data-tour-panel=\"chat-text-entry\"]",
        navDotAriaLabel: "Respond and add notes",
        content: (
            <div className={Styles.body}>
                Use this area to respond, add attachments and articles, format your message, schedule responses, and much more.
                Click the Reply button and switch to a note to keep the conversation internal.
            </div>
        ),
    },
    {
        title: "User and Ticket Context",
        selector: "[data-tour-panel=\"ticket-details\"]",
        navDotAriaLabel: "User and Ticket context",
        content: (
            <div className={Styles.body}>
                See everything there is to know about the ticket, the requester and all the related fields. Want to
                change the priority or assignee? Simply hover over the field and click the pencil to edit. Want to
                view SLAs or track tasks? View all the details sections.
            </div>
        ),
    },
    {
        title: "User and Ticket Context",
        selector: "[data-tour-panel=\"chat-actions\"]",
        navDotAriaLabel: "User and Ticket context",
        content: (
            <div className={Styles.body}>
                Take actions on tickets such as transfering, closing, assigning, and invoking integrations and action.
            </div>
        ),
    },

    // ...
];

const KEY_SHOWN_TOUR_STEP = "dashboard.tour.v1.step";
const KEY_SHOWN_TOUR_DONE = "dashboard.tour.v1.completed";

function BadgeContent({ current, total }: {current: number, total: number}) {
    return (
        <span>
            {steps[current - 1]?.title}
            {" "}
            <small>
                (
                {current}
                /
                {total}
                )
            </small>
        </span>
    );
}

function DisabledButton({ text }: {text: string}) {
    return (
        /* eslint-disable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
        <small
            onClick={evt => {
                evt.preventDefault();
                evt.stopPropagation();
                return false;
            }}
        >
            {text}
        </small>
    /* eslint-enable jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */
    );
}

const FEATURE_SHOW_TOUR = "frontend.tour.v1.show";

const PAUSE_TICKET_STEP = 3;
export default function ProductTour({ metricPathPrefix }: {metricPathPrefix?: string[]}) {
    const [tourStep, setTourStep] = useState<number | undefined>();
    const session = useCurrentSession();
    const { featureFlags } = usePortal();
    const theme = useTheme();
    const { chatId } = useParams(Paths.AGENT_CHAT);
    const { historyPush } = useRouting();
    const [shouldIncrementAfterPause, setShouldIncrementAfterPause] = useState(true);

    useEffect(() => {
        if (!theme.isV3 || tourStep != null || !featureFlags.has(FEATURE_SHOW_TOUR)) {
            return;
        }
        const sessionAge = Date.now() - (tryParseDate(session.current?.createdAt)?.getTime() ?? 0);
        if (sessionAge > DAY) {
            session.saveUserFlag(KEY_SHOWN_TOUR_STEP, "-1");
            setTourStep(undefined);
            return;
        }
        const currentStep = session.userFlags[KEY_SHOWN_TOUR_STEP];
        setTourStep(currentStep != null ? undefined : 0);
    }, [session.userFlags[KEY_SHOWN_TOUR_STEP], theme.isV3, featureFlags]);

    useEffect(() => {
        const currentStep = tourStep ?? 0;
        if (!chatId && currentStep === PAUSE_TICKET_STEP && shouldIncrementAfterPause) {
            const { selector } = steps[PAUSE_TICKET_STEP];
            const observer = new MutationObserver(() => {
                if (document.querySelector(selector)) {
                    observer.disconnect();
                    setTimeout(() => {
                        setTourStep(current => (current ?? 0) + 1);
                    }, 100);
                }
            });

            observer.observe(document.body, {
                childList: true,
                subtree: true,
            });
            setShouldIncrementAfterPause(true);
        }
    }, [chatId, tourStep, shouldIncrementAfterPause]);

    useEffect(() => {
        if (tourStep != null) {
            session.saveUserFlag(KEY_SHOWN_TOUR_STEP, tourStep.toString());
        }
    }, [tourStep]);

    if (!featureFlags.has(FEATURE_SHOW_TOUR)) {
        return null;
    }

    const tourDone = () => {
        session.saveUserFlag(KEY_SHOWN_TOUR_DONE, "true");
    };

    const setStep = (previous: number | undefined, newStep: number) => {
        let assignStep = newStep;
        if (newStep <= PAUSE_TICKET_STEP && chatId && (previous ?? 0) > newStep) {
            setShouldIncrementAfterPause(false);
            historyPush(Paths.AGENT);
        } else if (newStep >= PAUSE_TICKET_STEP && !chatId) {
            assignStep = PAUSE_TICKET_STEP;
            setShouldIncrementAfterPause(true);
        } else {
            setShouldIncrementAfterPause(true);
        }
        setTourStep(assignStep);
    };

    return (
        <>
            <Tour
                steps={steps}
                // getCurrentStep={({ index }) => index}
                isOpen={tourStep != null}
                disableFocusLock
                disableInteraction
                getCurrentStep={() => tourStep}
                nextButton={tourStep === PAUSE_TICKET_STEP ? <DisabledButton text="" /> : undefined}
                goToStep={tourStep}
                nextStep={() => {
                    setStep(tourStep, (tourStep ?? 0) + 1);
                }}
                prevStep={() => {
                    setStep(tourStep, (tourStep ?? 0) - 1);
                }}
                // eslint-disable-next-line react/no-unstable-nested-components
                badgeContent={(current, total) => <BadgeContent total={total} current={current} />}
                className={Styles.tour}
                maskClassName={Styles.mask}
                highlightedMaskClassName={Styles.highlight}
                lastStepNextButton={<Button size="small" onClick={tourDone}>Done</Button>}
                onRequestClose={() => {
                    setTourStep(undefined);
                }}
            />
            {session.userFlags[KEY_SHOWN_TOUR_DONE] == null && (
                <Button
                    type="button"
                    variant="ghost-blue"
                    size="xsmall"
                    prefixIcon={<RiPassportFill />}
                    metricPath={metricPathPrefix ? [...metricPathPrefix, "tour", "show"] : undefined}
                    onClick={() => {
                        let targetIndex = Math.max(parseInt(session.userFlags[KEY_SHOWN_TOUR_STEP] || "0", 10), 0);
                        if (!chatId && targetIndex > PAUSE_TICKET_STEP) {
                            targetIndex = PAUSE_TICKET_STEP;
                        }

                        setShouldIncrementAfterPause(true);
                        setTourStep(targetIndex);
                    }}
                >
                    {session.userFlags[KEY_SHOWN_TOUR_STEP] != null && session.userFlags[KEY_SHOWN_TOUR_STEP] !== "-1"
                        ? "Continue"
                        : "Start"}
                    {" "}
                    Tour
                </Button>
            )}

        </>
    );
}

ProductTour.defaultProps = {
    metricPathPrefix: undefined,
};
